/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { moneyMask } from '@/shared/utils';
import {
  formatPropertyPrice,
  LIST_PRICE_SUFFIX_KEY,
  resolveListPrice,
} from '@/shared/utils/format-property-price';
import type { PropertyResponseType } from '@services/housien/types';

/**
 * The one figure of a card without both a sale and a rent: whichever price
 * the listing has, Temporada and Leilão included, with its suffix.
 *
 * @param property - The listing.
 * @param t - Translator for the suffix.
 * @returns The price text, or a dash when the listing has none.
 */
function singlePriceLabel(property: PropertyResponseType, t: (key: string) => string): string {
  const price = resolveListPrice(property as unknown as Record<string, unknown>);
  if (!price) return '—';
  const suffixKey = LIST_PRICE_SUFFIX_KEY[price.kind];
  return `${moneyMask(price.amount.toFixed(2)).masked}${suffixKey ? t(suffixKey) : ''}`;
}

/**
 * Builds the one-line spec summary: rooms, area, parking.
 *
 * Only the parts that exist are joined, so a listing missing its parking
 * count reads "2 dorm. · 44 m²" rather than "2 dorm. · 44 m² · — vagas".
 */
export function specLine(
  property: PropertyResponseType,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const c = property.characteristics;
  const area = c?.total_area ?? c?.constructed_area;
  return [
    c?.bedrooms ? t('list.card.bedrooms', { count: c.bedrooms }) : null,
    area ? `${area} m²` : null,
    c?.parking_spots ? t('list.card.parking', { count: c.parking_spots }) : null,
  ].filter(Boolean).join(' · ');
}

/**
 * The price line of a card: both figures when the listing has a sale and a
 * rent, else whichever single price it has.
 *
 * @param property - The listing.
 * @param t - Translator for the suffixes.
 * @returns The price text, or a dash when the listing has none.
 */
export function cardPriceLabel(property: PropertyResponseType, t: (key: string) => string): string {
  const { salePrice, rentalPrice, hasBoth } = formatPropertyPrice(
    property as unknown as Record<string, unknown>,
  );
  if (!hasBoth) return singlePriceLabel(property, t);
  return `${moneyMask(salePrice!.toFixed(2)).masked} · ${moneyMask(rentalPrice!.toFixed(2)).masked}${t('list.perMonth')}`;
}

/** How many photos the listing carries, tolerating either media shape. */
export function photoCount(property: PropertyResponseType): number {
  const images = (property.media as { images?: unknown[] } | undefined)?.images;
  return Array.isArray(images) ? images.length : 0;
}
